import {
  type Filter,
  FilterId,
  type MultiFilter,
  type TreeFilter,
} from '../../../../filters/types/filters';
import { LocationType, type MapPoint } from '../types';

export interface NormalizedGeographySelections {
  regions: string[];
  countries: string[];
  metros: string[];
  cities: MapPoint[];
  states: string[];
}

function getActiveGeographyFilters(
  filters: Map<FilterId, Filter>
): TreeFilter[] {
  const geographyFilter = filters.get(FilterId.GEOGRAPHY) as TreeFilter;
  const multiGeographyFilter = filters.get(
    FilterId.MULTI_GEOGRAPHY
  ) as MultiFilter<TreeFilter>;

  const activeGeographyFilters: TreeFilter[] = [];

  if (geographyFilter?.selections?.length) {
    activeGeographyFilters.push(geographyFilter);
  }

  if (multiGeographyFilter?.instances) {
    activeGeographyFilters.push(...multiGeographyFilter.instances);
  }

  return activeGeographyFilters;
}

export function getNormalizedGeographySelections(
  filters: Map<FilterId, Filter>,
  points: MapPoint[]
): NormalizedGeographySelections {
  const activeGeographyFilters = getActiveGeographyFilters(filters);

  const selectedRegions = new Set<string>();
  const selectedCountries = new Set<string>();
  const selectedMetros = new Set<string>();
  const selectedStates = new Set<string>();
  const selectedCities = new Set<string>();

  for (const filter of activeGeographyFilters) {
    for (const selection of filter.selections || []) {
      const levelId = selection.levelId;
      const value = selection.name?.toLowerCase() || '';

      switch (levelId) {
        case 'region':
          selectedRegions.add(value);
          break;
        case 'country':
          selectedCountries.add(value);
          break;
        case 'msa': {
          const msaId = selection.id.split('_').pop();
          if (msaId) {
            selectedMetros.add(msaId);
          }
          break;
        }
        case 'state': {
          const stateId = selection.id.split('_').pop();
          if (stateId) {
            selectedStates.add(stateId);
          }
          break;
        }
        case 'city':
          selectedCities.add(value);
          break;
      }
    }
  }

  const cityPoints: MapPoint[] = [];
  selectedCities.forEach((city) => {
    points
      .filter(
        (point) =>
          point.locationType === LocationType.CITY &&
          point.label?.toLowerCase() === city
      )
      .forEach((point) => {
        if (Number.isFinite(point.lon) && Number.isFinite(point.lat)) {
          cityPoints.push(point);
        }
      });
  });

  return {
    regions: Array.from(selectedRegions),
    countries: Array.from(selectedCountries),
    metros: Array.from(selectedMetros),
    states: Array.from(selectedStates),
    cities: cityPoints,
  };
}

export function hasActiveGeographyFilters(
  filters: Map<FilterId, Filter>
): boolean {
  return getActiveGeographyFilters(filters).length > 0;
}
